/* eslint-disable react/prop-types, react/destructuring-assignment */
import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useIntl } from 'react-intl'
import { useSelector } from 'react-redux'
import { getSelectedCustomer } from '../../../../_augmentt/selectors/customers'
import useFreshEmployeeData from '../../../hooks/useFreshEmployeeData'
import FindEmployeeModal from '../modals/FindEmployeeModal'
import { modalConfiguration } from './config'
import DataLoader from './DataLoader'
import { GWS_HUB_INTEGRATION, getGwsHubTask } from './integrations/gws/hubTasks'
import * as gwsImports from './integrations/gws/importer'
import * as m365Imports from './integrations/m365/importer'
import GenericModal from './modals/GenericModal'
import GenericModalBodyError from './partials/GenericModalBodyError'
import GenericModalBodyLoading from './partials/GenericModalBodyLoading'
import GenericModalBodySuccess from './partials/GenericModalBodySuccess'
import GenericModalFooter from './partials/GenericModalFooter'
import GenericModalHeader from './partials/GenericModalHeader'
import states from './states.json'
import { selectEmployee } from './utils/helpers'
import {
  formatHubTaskStatusMessage,
  isHubTaskFailed,
  parseHubTaskErrors,
  reconcileGwsAddUserLicenseActions,
  submitEngageHubTask,
} from './utils/submitEngageHubTask'
import { triggerTask } from './utils/tasks'

import { createHubTask, pollTaskStatus } from '../../../crud/integrationHub.crud'
import { TASK_ACTIONS, TASK_TYPES } from '../../../util/hub-task-management/hub.constants'
import { GWS, OFFICE365 } from './utils'

const resolveIntegrationManagerIntegration = (propIntegration, reduxIntegration) => propIntegration ?? reduxIntegration

// Handles the state of the modal and the rendering of loading, errors, and successful actions. Each action is responsible for handling its own view
function IntegrationManager(props) {
  const {
    action,
    show,
    onCancel,
    onUpdate,
    selectedEmployee,
    selectedEmployees,
    allAugmenttEmployees,
    allLicensedEmployees,
    employees,
    allEmployeesGroups,
    selectedIntegration: selectedIntegrationFromCaller,
  } = props

  const intl = useIntl()

  const selectedEmployeeReference = useRef()

  const currentUser = useSelector(({ user }) => user.data)
  const selectedCustomer = useSelector(getSelectedCustomer)
  const reduxSelectedIntegration = useSelector(({ integrations }) => integrations.selectedIntegration)
  const selectedIntegration = resolveIntegrationManagerIntegration(
    selectedIntegrationFromCaller,
    reduxSelectedIntegration,
  )

  const [state, setState] = useState(states.HIDE)
  const [status, setStatus] = useState()

  // Task data states
  const [loadingData, setLoadingData] = useState({})
  const [errorData, setErrorData] = useState({})
  const [successData, setSuccessData] = useState({})

  const [disableSubmit, setDisableSubmit] = useState(false)
  const [headerTitleId, setHeaderTitleId] = useState()
  const [submitButtonLangId, setSubmitButtonLangId] = useState()
  const [headerStyle, setHeaderStyle] = useState()
  const [integrationName, setIntegrationName] = useState()
  const [taskParameters, setTaskParameters] = useState({})

  // Action-specific state: Put any data in this object that is built in the render state of the action-component, and is needed for the success/error state body-component
  const [actionData, setActionData] = useState() // Store any action-specific data in here. Common uses are for storing data to be used in the success state, as well as to store task parameters
  const [completedActions, setCompletedActions] = useState([]) // Tracks which sub-actions were completed throughout the action flow. A single action may have sub-actions, for example, assigning licenses is a sub-action of add user
  const [actionHasAdditionalTasksToComplete, setActionHasAdditionalTasksToComplete] = useState(false) // Used when an action-component fires off more than 1 task to the probe in a single flow
  const [tasksCompleted, setTasksCompleted] = useState(0) // This is used to track the state of which task we are in within an action flow. A task being something fired off to the probe.

  // Modal state
  const [showFindEmployeeModal, setShowFindEmployeeModal] = useState(false)

  // Formik state
  const [initialValues, setInitialValues] = useState()

  // Fetch fresh employee data when opened from table.
  // GWS employees do not have M365 group fields — only treat as stale when license/group data is missing.
  const freshSelectedEmployee = useFreshEmployeeData({
    employee: selectedEmployee,
    show,
    customer: selectedCustomer,
    isStaleData: (emp) => {
      const hasM365GroupData = !!(
        emp?.m365Groups ||
        emp?.securityGroups ||
        emp?.distributionGroups ||
        emp?.sharedMailboxes ||
        emp?.groupsByType
      )
      const hasGwsLicenseData = Array.isArray(emp?.licenseType) && emp.licenseType.length > 0
      const hasGwsGroupData = Array.isArray(emp?.groups) && emp.groups.length > 0
      return !(hasM365GroupData || hasGwsLicenseData || hasGwsGroupData)
    },
  })

  // Use fresh employee data in selectedEmployees array
  const freshSelectedEmployees = useMemo(() => {
    if (!selectedEmployees || selectedEmployees.length === 0) {
      return freshSelectedEmployee ? [freshSelectedEmployee] : []
    }
    const freshEmployeeId = freshSelectedEmployee?.employeeId || freshSelectedEmployee?.id
    return selectedEmployees.map((emp) => {
      const empId = emp?.employeeId || emp?.id
      if (empId === freshEmployeeId) {
        return freshSelectedEmployee
      }
      return emp
    })
  }, [selectedEmployees, freshSelectedEmployee])

  // Handles display of modal
  useEffect(() => {
    if (show === true) {
      // Load all data
      setState(states.DATA_LOADER)
    } else if (show === false) {
      // Reset any render states
      // eslint-disable-next-line no-use-before-define
      resetRenderState()
      setState(states.HIDE)
    }
  }, [show])

  // Handles closing any additional modals
  const closeModals = () => {
    setShowFindEmployeeModal(false)
  }

  // Loading State
  const enableLoading = () => {
    setState(states.LOADING)
    setStatus(null)
  }

  // Handles closing the modal
  const onActionCancel = () => {
    onCancel() // Tell parent (engage) to close the modal
    setState(states.HIDE)
    setStatus()
  }

  // Reset modal render state values
  const resetRenderState = () => {
    setLoadingData({})
    setSuccessData({})
    setErrorData({})

    setDisableSubmit(true)
    setTaskParameters({})
    setIntegrationName()
    setHeaderTitleId()
  }

  // Handler for action (task) submission to Hub (GWS) or Probe (legacy M365 tasks)
  // eslint-disable-next-line no-shadow
  const onActionSubmit = async (action, parameters, preSubmitFunction) => {
    // eslint-disable-next-line no-console
    console.log('onActionSubmit', {
      action,
      parameters,
      preSubmitFunction,
      selectedIntegration,
    })
    enableLoading()

    const gwsHubTask = getGwsHubTask(action)
    if (gwsHubTask) {
      try {
        const task = await submitEngageHubTask({
          actions: parameters,
          tasktype: gwsHubTask.tasktype,
          action: gwsHubTask.action,
          integration: GWS_HUB_INTEGRATION,
          customerId: selectedCustomer?.id,
          organizationId: selectedCustomer?.organization,
          userId: currentUser?.id,
          poll: gwsHubTask.poll,
        })

        const errors = parseHubTaskErrors(task?.errors)
        const failed = isHubTaskFailed(task)
        const result = {
          Status: failed ? 'Failed' : 'Completed',
          StatusMessage: failed
            ? formatHubTaskStatusMessage({
                errors,
                errorMappings: errorData?.errorMappings ?? {},
                intl,
              }) || 'Failed to perform action'
            : '',
        }

        if (failed) {
          setState(states.ERROR)
          setStatus(result)
          return
        }

        const licenseResult = reconcileGwsAddUserLicenseActions({
          completedActions,
          actionData,
          task,
          parameters,
        })
        if (licenseResult.changed) {
          setCompletedActions(licenseResult.completedActions)
          setActionData(licenseResult.actionData)
        }

        if (actionHasAdditionalTasksToComplete === false) {
          if (preSubmitFunction && typeof preSubmitFunction === 'function') {
            await preSubmitFunction()
          }

          setStatus(result)
          setState(states.SUCCESS)
          onUpdate()
          return
        }

        setTasksCompleted(tasksCompleted + 1)
        setState(states.RENDER)
      } catch (error) {
        console.error(error)
        setState(states.ERROR)
        setStatus({
          Status: 'Failed',
          StatusMessage: error?.message,
        })
      }
      return
    }

    const customerId = selectedCustomer?.id
    const integrationApplicationId = selectedIntegration?.IntegrationApplication?.application_name
    const userId = currentUser?.id
    const errorMappings = errorData?.errorMappings ? errorData.errorMappings : {} // Set during render view

    // Check for action remapping
    let payloadAction = action
    if (modalConfiguration.actionMapping[action]) {
      payloadAction = modalConfiguration.actionMapping[action]
    }

    const payload = {
      customerId,
      integrationApplicationId,
      payloadAction,
      userId,
      errorMappings,
      parameters,
    }

    // eslint-disable-next-line no-shadow
    const setParialDataPayload = (payload) => {
      // eslint-disable-next-line no-param-reassign
      payload.parameters = {
        taskName: payload.payloadAction,
        actions: { ...payload.parameters },
      }
      // eslint-disable-next-line no-param-reassign
      payload.payloadAction = 'M365_SECURE_PARTIAL'
    }

    let monitorTaskLimit

    if (payload?.payloadAction) {
      switch (payload.payloadAction) {
        case 'M365_MANAGE_GROUPS':
          payload.payloadAction = 'MANAGE_M365_GROUPS'
          payload.parameters.groupType = 'M365Group'

          setParialDataPayload(payload)
          break
        case 'M365_MANAGE_SHARED_GROUPS':
          payload.payloadAction = 'M365_MANAGE_GROUPS'
          payload.parameters.groupType = 'SharedMailbox'

          setParialDataPayload(payload)
          break
        case 'M365_MANAGE_DISTRIBUTION_GROUPS':
          payload.payloadAction = 'M365_MANAGE_GROUPS'
          payload.parameters.groupType = 'DistributionGroup'

          setParialDataPayload(payload)
          break
        case 'M365_MANAGE_SECURITY_GROUPS':
          payload.payloadAction = 'MANAGE_SECURITY_GROUPS'
          payload.parameters.groupType = 'SecurityGroup'

          setParialDataPayload(payload)
          break
        case 'M365_ENABLE_USER_MFA':
          payload.parameters.integrationId = selectedIntegration?.id
          payload.parameters.mfaType = 'perUserMFA'
          monitorTaskLimit = 875 // 875 limit is as about 15 minutes of monitorin
          break
        case 'M365_DISABLE_USER':
          setParialDataPayload(payload)
          break
        default:
          break
      }
    }

    if (['MANAGE_M365_GROUPS', 'MANAGE_SECURITY_GROUPS'].includes(payload?.parameters?.taskName)) {
      const taskName = payload?.parameters?.taskName

      const hubTask = {
        actions: parameters,
        customerId: selectedCustomer?.id,
        userId: currentUser?.id,
        organizationId: selectedCustomer?.organization,
        action: TASK_ACTIONS.update,
      }

      switch (taskName) {
        case 'MANAGE_M365_GROUPS':
          hubTask.tasktype = TASK_TYPES.manageM365Groups
          break
        case 'MANAGE_SECURITY_GROUPS':
          hubTask.tasktype = TASK_TYPES.manageSecurityGroups
          break
        default:
          break
      }

      try {
        const { data: { taskId } = {} } = await createHubTask(hubTask)

        if (taskId) {
          const { data: { _source: task } = {} } =
            (await pollTaskStatus(taskId, {
              retries: 10,
              timeout: 3000,
            })) ?? {}

          if (task) {
            const result = {
              Status: task?.status === 'SUCCESS' ? 'Completed' : 'Failed',
              StatusMessage: task?.status === 'FAILED' ? task?.errors : '',
            }

            if (result.Status === 'Failed') {
              setState(states.ERROR)
              setErrorData(true)
              setStatus(result)
            } else {
              setStatus(result)
              setState(states.SUCCESS)
              onUpdate()
            }
          }
        }
      } catch (error) {
        console.error(error)
      }
    } else {
      // Trigger the action (task) and wait for it to complete
      const responseObject = await triggerTask(intl, payload, setState, setStatus, monitorTaskLimit)
      const { error, result } = responseObject

      // Action error: error is translated by taskManager, set status with error message
      if (error) {
        setState(states.ERROR)
        setStatus(error)
      } else {
        // The action does not have any remaining tasks so go to the success state.
        // eslint-disable-next-line no-lonely-if
        if (actionHasAdditionalTasksToComplete === false) {
          // Preform any pre-submit functionality prior to firing off the task to the probe. This is generally used for any augmentt backend network calls
          if (preSubmitFunction && typeof preSubmitFunction === 'function') {
            await preSubmitFunction() // Todo for the future: This may not always be returning a promise, so we need something in the config to tell it what function type to expect
          }

          // Action success
          setStatus(result)
          setState(states.SUCCESS)
          onUpdate() // Inform engage to reset data
        } else {
          // The action has remaining tasks so go back to render state
          setTasksCompleted(tasksCompleted + 1)
          setState(states.RENDER)
        }
      }
    }
  }

  // Render the view
  if (state === states.HIDE) {
    return null
  }
  if (state === states.DATA_LOADER) {
    return (
      <DataLoader
        intl={intl}
        currentUser={currentUser}
        selectedEmployee={freshSelectedEmployee}
        state={state}
        action={action}
        setActionHasAdditionalTasksToComplete={setActionHasAdditionalTasksToComplete}
        setInitialValues={setInitialValues}
        setTaskParameters={setTaskParameters}
        setLoadingData={setLoadingData}
        setSuccessData={setSuccessData}
        setErrorData={setErrorData}
        setHeaderTitleId={setHeaderTitleId}
        setHeaderStyle={setHeaderStyle}
        setSubmitButtonLangId={setSubmitButtonLangId}
        setIntegrationName={setIntegrationName}
        setState={setState}
      />
    )
  }
  if (state === states.RENDER) {
    let RenderBody

    switch (selectedIntegration?.IntegrationApplication?.application_name) {
      case OFFICE365:
        RenderBody = m365Imports[action]
        break
      case GWS:
        RenderBody = gwsImports[action]
        break
      default:
        console.error(
          '[IntegrationManager] - Unable to render the action for the selected integration: ',
          selectedIntegration?.IntegrationApplication?.display_name,
        )
        break
    }

    return (
      <GenericModal
        show={show}
        intl={intl}
        state={state}
        setState={setState}
        status={status}
        action={action}
        taskParameters={taskParameters}
        setTaskParameters={setTaskParameters}
        disableSubmit={disableSubmit}
        setDisableSubmit={setDisableSubmit}
        submitButtonLangId={submitButtonLangId}
        onActionSubmit={onActionSubmit}
        onCancel={onActionCancel}
        actionData={actionData}
        setActionData={setActionData}
        integrationName={integrationName}
        headerTitleId={headerTitleId}
        headerStyle={headerStyle}
        setHeaderStyle={setHeaderStyle}
        setLoadingData={setLoadingData}
        loadingData={loadingData}
        selectedEmployee={freshSelectedEmployee}
        selectedEmployees={freshSelectedEmployees}
        allAugmenttEmployees={allAugmenttEmployees}
        allLicensedEmployees={allLicensedEmployees}
        selectedCustomer={selectedCustomer}
        currentUser={currentUser}
        initialValues={initialValues}
        setSubmitButtonLangId={setSubmitButtonLangId}
        selectedEmployeeReference={selectedEmployeeReference}
        setShowFindEmployeeModal={setShowFindEmployeeModal}
        completedActions={completedActions}
        setCompletedActions={setCompletedActions}
        setActionHasAdditionalTasksToComplete={setActionHasAdditionalTasksToComplete}
        tasksCompleted={tasksCompleted}
        employees={employees}
        allEmployeesGroups={allEmployeesGroups}
      >
        <GenericModalHeader />
        <RenderBody>
          <GenericModalFooter />
        </RenderBody>

        {/* Include the 'Find an Employee' modal for the current action */}
        {modalConfiguration.renderFindAnEmployeeModal[action] ? (
          <FindEmployeeModal
            onHideFindEmployeeModal={closeModals}
            showFindEmployeeModal={showFindEmployeeModal}
            employees={allAugmenttEmployees}
            onEmployeeSelect={(employee) => selectEmployee(employee, selectedEmployeeReference)}
          />
        ) : null}
      </GenericModal>
    )
  }
  if (state === states.LOADING) {
    return (
      <GenericModal
        show={show}
        state={state}
        onCancel={onActionCancel}
        headerTitleId={loadingData?.loadingHeaderTitleId ? loadingData.loadingHeaderTitleId : headerTitleId}
        loadingPhraseId={loadingData?.loadingPhraseId ? loadingData.loadingPhraseId : undefined}
        selectedEmployee={freshSelectedEmployee}
        selectedEmployees={freshSelectedEmployees}
        actionData={actionData}
        integrationName={integrationName}
      >
        <GenericModalHeader />
        <GenericModalBodyLoading />
      </GenericModal>
    )
  }
  if (state === states.SUCCESS) {
    return (
      <GenericModal
        show={show}
        state={state}
        onCancel={onActionCancel}
        integrationName={integrationName}
        successData={successData}
        actionData={actionData}
        selectedEmployee={freshSelectedEmployee}
        selectedEmployees={freshSelectedEmployees}
        completedActions={completedActions}
        headerTitleId={successData?.successHeaderTitleId ? successData.successHeaderTitleId : headerTitleId}
        action={action}
      >
        <GenericModalHeader />
        <GenericModalBodySuccess>
          <GenericModalFooter />
        </GenericModalBodySuccess>
      </GenericModal>
    )
  }
  if (state === states.ERROR) {
    return (
      <GenericModal
        show={show}
        state={state}
        status={status}
        onCancel={onActionCancel}
        headerTitleId={errorData?.errorHeaderTitleId ? errorData.errorHeaderTitleId : headerTitleId}
        actionLangId={errorData?.actionLangId ? errorData.actionLangId : undefined} // Action that failed, can grab from success object
        selectedEmployee={freshSelectedEmployee}
        selectedEmployees={freshSelectedEmployees}
        integrationName={integrationName}
      >
        <GenericModalHeader />
        <GenericModalBodyError>
          <GenericModalFooter />
        </GenericModalBodyError>
      </GenericModal>
    )
  }
}

export default IntegrationManager
